import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	useMediaQuery,
} from '@mui/material';
import {
	EntityType,
	FormData as FormDataType,
	FormSelectOptions,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
} from '../types';
import type { SxProps, Theme } from '@mui/material/styles';
import FormTemplate from './FormTemplate';
import {EntryPropertyBelongsToBlockEditDialog, type RelationValue} from './EntryPropertyBelongsToEditDialog';
import {initEditFormData, sanitizeEditFormDataObject} from '../common/util';
import { RepositoryEntriesRowDto } from '../services/repositoryEntries';

const RELATION_TYPES = [EntityType.PropertyDefinitionBelongsTo, EntityType.PropertyDefinitionHas];

const isRelationDef = (def: PropertyDefinition): def is PropertyDefinitionBelongsTo | PropertyDefinitionHas =>
	RELATION_TYPES.includes(def.type as EntityType);

const isTextPropertyDef = (def: PropertyDefinition) =>
	def.type === EntityType.PropertyDefinitionText;

const textFieldDialogContentSx: SxProps<Theme> = {
	'& .MuiInputBase-root': {alignItems: 'flex-start'},
	'& textarea': {minHeight: 280},
};

export interface EditRepositoryEntryFieldDialogProps {
	open: boolean;
	onClose: () => void;
	entry: RepositoryEntriesRowDto | null;
	propertyDefinition: PropertyDefinition | null;
	formSelectOptions?: FormSelectOptions;
	onSubmitValues: (values: Record<string, unknown>) => void;
	onNavigateToEntry?: (entryId: string) => void;
	onAddEntryClick?: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, currentEntryIds: string[]) => void;
	disabled?: boolean;
}

const EditRepositoryEntryFieldDialog = ({
	open,
	onClose,
	entry,
	propertyDefinition,
	formSelectOptions = {},
	onSubmitValues,
	onNavigateToEntry,
	onAddEntryClick,
	disabled = false,
}: EditRepositoryEntryFieldDialogProps) => {
	const [formData, setFormData] = useState<FormDataType>({});

	useEffect(() => {
		if (!open || !entry || !propertyDefinition || isRelationDef(propertyDefinition)) {
			return;
		}
		setFormData(initEditFormData([propertyDefinition], entry.data));
	}, [open, entry, propertyDefinition]);

	const onValueChange = useCallback(
		(key: string, value: unknown) => {
			if (value !== ' ') {
				setFormData((prev) => ({...prev, [key]: value}));
			}
		},
		[]
	);

	const handleScalarSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!propertyDefinition) {
			return;
		}
		const payload = sanitizeEditFormDataObject([propertyDefinition], formData);
		onSubmitValues(payload);
		onClose();
	};

	const relationValues = useMemo((): { value: string; label: string }[] => {
		if (!entry || !propertyDefinition || !isRelationDef(propertyDefinition)) {
			return [];
		}
		const rawValue = entry.data[propertyDefinition.slug];
		if (!Array.isArray(rawValue)) {
			return [];
		}
		return rawValue.map((arrayItem: Record<string, unknown>) => {
			const targetId =
				typeof arrayItem.targetEntryId === 'string'
					? arrayItem.targetEntryId
					: typeof arrayItem.value === 'string'
						? arrayItem.value
						: '';
			const label = typeof arrayItem.label === 'string' ? arrayItem.label : '';
			return { value: targetId, label };
		});
	}, [entry, propertyDefinition]);

	const handleRelationChange = useCallback(
		(def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, nextValues: RelationValue[]) => {
			onSubmitValues({[def.slug]: nextValues.map(({ value }) => value)});
		},
		[onSubmitValues]
	);

	const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

	if (!entry || !propertyDefinition) {
		return null;
	}

	if (isRelationDef(propertyDefinition)) {
		return (
			<EntryPropertyBelongsToBlockEditDialog
				open={open}
				onClose={onClose}
				def={propertyDefinition}
				values={relationValues}
				onNavigateToEntry={onNavigateToEntry ?? (() => {console.log('onNavigateToEntry');})}
				onAddEntryClick={onAddEntryClick ?? (() => {console.log('onAddEntryClick');})}
				onChangeValues={handleRelationChange}
				disabled={disabled}
			/>
		);
	}

	const isTextProperty = isTextPropertyDef(propertyDefinition);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={isTextProperty ? 'sm' : 'xs'}
			fullWidth
			scroll="body"
			fullScreen={isTextProperty && !isMdUp}
		>
			<DialogTitle sx={{color: 'text.secondary'}}>{propertyDefinition.label}</DialogTitle>
			<form onSubmit={handleScalarSubmit} autoComplete="off">
				<DialogContent sx={isTextProperty ? textFieldDialogContentSx : undefined}>
					<FormTemplate
						propertyDefinitions={[{...propertyDefinition, autoFocus: true}]}
						formData={formData}
						onValueChange={onValueChange}
						selectOptions={formSelectOptions}
						autoFocusSlug={propertyDefinition.slug}
						hideLabel={true}
						textFieldVariant="standard"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button type="submit" variant="contained">
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EditRepositoryEntryFieldDialog;
